import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Setup axios interceptors:
//  1. Attach Bearer token to every request.
//  2. Retry transient failures once with backoff. Production serverless cold-starts
//     occasionally return 502 with CORS headers stripped — surfacing as a network
//     error in the browser. A single ~1.5s retry masks 99% of those flaps.
const TRANSIENT_RETRY_DELAY_MS = 1500;
const isTransientFailure = (error) => {
  // Network failure (no response at all → most likely CORS-stripped 502).
  if (!error?.response) return true;
  // Server-side flaps.
  const s = error.response.status;
  return s === 502 || s === 503 || s === 504;
};
const isIdempotent = (config) => {
  if (!config?.method) return false;
  const m = config.method.toLowerCase();
  return m === 'get' || m === 'head' || m === 'options';
};
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('aptivon_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // NOTE: do not force withCredentials. Auth uses Bearer tokens via localStorage.
    // Setting withCredentials=true breaks CORS preflight when the ingress responds
    // with `Access-Control-Allow-Origin: *` (browser rejects '*' with credentials).
    return config;
  });

  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const config = error?.config;
      // Skip if no config, already retried, or method isn't safe to retry.
      if (!config || config.__retried || !isIdempotent(config) || !isTransientFailure(error)) {
        return Promise.reject(error);
      }
      config.__retried = true;
      await new Promise((r) => setTimeout(r, TRANSIENT_RETRY_DELAY_MS));
      try {
        return await axios(config);
      } catch (e) {
        return Promise.reject(e);
      }
    },
  );
};
setupAxiosInterceptor();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('aptivon_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (error) {
      localStorage.removeItem('aptivon_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (data.access_token) {
        localStorage.setItem('aptivon_token', data.access_token);
      }
      setUser(data.user || data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        return {
          success: false,
          emailNotVerified: true,
          email: detail.email || email,
          error: detail.message || 'Please verify your email before logging in.'
        };
      }
      return {
        success: false,
        error: formatApiErrorDetail(detail) || error.message
      };
    }
  }, []);

  const register = useCallback(async (name, email, password, date_of_birth, accepted_terms, state, referral_code) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, {
        name, email, password, date_of_birth, accepted_terms, state,
        referral_code: referral_code ? referral_code.trim().toUpperCase() : null,
      });
      // Email verification is temporarily disabled — register auto-logs the user in.
      if (data.access_token) {
        localStorage.setItem('aptivon_token', data.access_token);
        setUser(data.user || data);
      }
      return {
        success: true,
        email: data.email || email,
        message: data.message || 'Account created. Welcome!'
      };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      // silently fail
    }
    localStorage.removeItem('aptivon_token');
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (error) {
      // Don't surface — caller (Header poller etc.) can handle UI.
      // Log only when token exists; silent 401 on logout is expected.
      if (localStorage.getItem('aptivon_token')) {
        console.error('Failed to refresh user:', error?.response?.status || error?.message);
      }
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/verify-otp`, { email, otp });
      if (data.access_token) {
        localStorage.setItem('aptivon_token', data.access_token);
        setUser(data.user || data);
      }
      return { success: true, alreadyVerified: !!data.already_verified };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const resendVerification = useCallback(async (email) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser, verifyOtp, resendVerification }),
    [user, loading, login, register, logout, refreshUser, verifyOtp, resendVerification]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
